import { formatNumber } from '@angular/common';
import { LOCALE_ID, Pipe, PipeTransform, inject } from '@angular/core';

/**
 * Backend #678 / web #172: a distance in kilometres as the short label on a catalog tile —
 * „350 m", „1,2 km", „12 km".
 *
 * Three bands, chosen for what the number is good for at that range: below a kilometre the
 * reader thinks in metres and a 10 m step is plenty (the position behind it is rounded to about
 * a kilometre anyway, backend decision 9); up to ten kilometres one decimal tells „around the
 * corner" from „across town"; beyond that a whole kilometre is all the precision the value
 * honestly has. The number is formatted with `LOCALE_ID`, so it carries the bundle's decimal
 * separator, and the unit sits in the translation.
 *
 * `null` in, `null` out: the backend sends `distanceKm` only on a located search and only for a
 * seller with a coordinate — null means unknown, not far away, and the card then renders no
 * distance line at all rather than a dash.
 */
@Pipe({
  name: 'distance',
})
export class DistancePipe implements PipeTransform {
  private readonly localeId = inject(LOCALE_ID);

  transform(distanceKm: number | null | undefined): string | null {
    if (distanceKm === null || distanceKm === undefined || !Number.isFinite(distanceKm) || distanceKm < 0) {
      return null;
    }

    if (distanceKm < 0.995) {
      const metres = Math.max(10, Math.round(distanceKm * 100) * 10);
      const value = formatNumber(metres, this.localeId, '1.0-0');
      return $localize`:@@distance.metres:${value}:value: m`;
    }

    const value = formatNumber(distanceKm, this.localeId, distanceKm < 9.95 ? '1.0-1' : '1.0-0');
    return $localize`:@@distance.kilometres:${value}:value: km`;
  }
}
